/**
 * Canonical locale identifiers used across this app. Every other layer spells
 * Chinese differently (dayjs wants "zh-cn", MUI wants "zh-cn", OneSignal wants
 * "zh-Hans", the backend stores "zh-CN") — always go through the helpers below
 * rather than hardcoding a locale string anywhere else.
 */
export const SUPPORTED_LOCALES = ["en", "zh-CN"] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = "en";

/** Timezones covering all Chinese-speaking regions (mainland, HK, Macau, Taiwan). */
const CHINESE_TIMEZONES = new Set([
	"Asia/Shanghai",
	"Asia/Chongqing",
	"Asia/Chungking",
	"Asia/Harbin",
	"Asia/Urumqi",
	"Asia/Kashgar",
	"PRC",
	"Asia/Hong_Kong",
	"Asia/Macau",
	"Asia/Taipei",
]);

/**
 * Normalizes an arbitrary BCP-47-ish tag (from navigator.language, a saved
 * profile, or a stored preference) to a supported AppLocale, or null if it
 * doesn't match anything we ship. Mirrors LocaleResolver.normalize on the
 * backend: every zh-* variant (zh-TW, zh-HK, zh-Hant, ...) maps to zh-CN since
 * we only ship Simplified Chinese, not undefined/English for those regions.
 */
export function normalizeLocale(
	tag: string | null | undefined,
): AppLocale | null {
	if (!tag) return null;
	const value = tag.trim();
	if (/^zh([-_]\w+)*$/i.test(value)) return "zh-CN";
	if (/^en([-_]\w+)?$/i.test(value)) return "en";
	return null;
}

/**
 * Detects the likely locale for a first-time visitor from browser timezone +
 * language, with no network round-trip. Used only when there's no explicit
 * user choice and no saved backend preference yet.
 */
export function detectLocale(): AppLocale {
	try {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (timeZone && CHINESE_TIMEZONES.has(timeZone)) return "zh-CN";
	} catch {
		// Intl not available - fall through to language detection.
	}

	const languages =
		typeof navigator !== "undefined"
			? (navigator.languages?.length
					? navigator.languages
					: [navigator.language]
				).filter(Boolean)
			: [];

	for (const lang of languages) {
		const normalized = normalizeLocale(lang);
		if (normalized) return normalized;
	}

	return DEFAULT_LOCALE;
}

/** dayjs / MUI date-picker adapter locale strings, lowercase per their convention. */
export const DAYJS_LOCALE: Record<AppLocale, string> = {
	en: "en",
	"zh-CN": "zh-cn",
};

export const HTML_LANG: Record<AppLocale, string> = {
	en: "en",
	"zh-CN": "zh-CN",
};
